import React, { useCallback, useEffect, useState } from "react";
import { Box, FormControl, MenuItem, Select } from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";

// Languages exposed in the switcher. English is the page's source language.
export const TRANSLATE_LANGUAGES = [
  { code: "en", label: "English", rtl: false },
  { code: "ar", label: "العربية (Arabic)", rtl: true },
  { code: "fr", label: "Français (French)", rtl: false },
  { code: "it", label: "Italiano (Italian)", rtl: false },
  { code: "ur", label: "اردو (Urdu)", rtl: true },
];

const SCRIPT_ID = "google-translate-script";
const CONTAINER_ID = "google_translate_element";
const SCRIPT_SRC =
  "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
const STORAGE_KEY = "appLanguage";
const SOURCE_LANGUAGE = "en";

const isSupported = (code) =>
  TRANSLATE_LANGUAGES.some((language) => language.code === code);

const readCookieLanguage = () => {
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)googtrans=/[a-z-]+/([a-z-]+)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const getInitialLanguage = () => {
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && isSupported(stored)) return stored;

  const cookieLanguage = readCookieLanguage();
  if (cookieLanguage && isSupported(cookieLanguage)) return cookieLanguage;

  return SOURCE_LANGUAGE;
};

const setGoogTransCookie = (code) => {
  const value = `/${SOURCE_LANGUAGE}/${code}`;
  document.cookie = `googtrans=${value};path=/`;
  document.cookie = `googtrans=${value};path=/;domain=.${window.location.hostname}`;
};

const clearGoogTransCookie = () => {
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  document.cookie = `googtrans=;${expiry};path=/`;
  document.cookie = `googtrans=;${expiry};path=/;domain=.${window.location.hostname}`;
};

const applyDocumentDirection = (code) => {
  const language = TRANSLATE_LANGUAGES.find((item) => item.code === code);
  document.documentElement.lang = code;
  document.documentElement.dir = language?.rtl ? "rtl" : "ltr";
};

const getCombo = () => document.querySelector("select.goog-te-combo");

const loadGoogleTranslate = () => {
  if (document.getElementById(SCRIPT_ID)) return;

  window.googleTranslateElementInit = () => {
    if (!window.google?.translate?.TranslateElement) return;

    // eslint-disable-next-line no-new
    new window.google.translate.TranslateElement(
      {
        pageLanguage: SOURCE_LANGUAGE,
        includedLanguages: TRANSLATE_LANGUAGES.map((item) => item.code).join(","),
        autoDisplay: false,
      },
      CONTAINER_ID
    );
  };

  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = SCRIPT_SRC;
  script.async = true;
  document.body.appendChild(script);
};

const GoogleTranslate = () => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    loadGoogleTranslate();
    // Restore the previously selected language / text direction.
    applyDocumentDirection(getInitialLanguage());
  }, []);

  const handleChange = useCallback((code) => {
    setLanguage(code);
    window.localStorage.setItem(STORAGE_KEY, code);
    applyDocumentDirection(code);

    if (code === SOURCE_LANGUAGE) {
      clearGoogTransCookie();
    } else {
      setGoogTransCookie(code);
    }

    // Drive Google's hidden <select> so the page updates without a reload.
    const combo = getCombo();
    if (combo) {
      combo.value = code;
      combo.dispatchEvent(new Event("change", { bubbles: true }));
    } else {
      // Widget not ready yet: the cookie will apply the translation on reload.
      window.location.reload();
    }
  }, []);

  return (
    <>
      {/* Google renders its widget here; it is moved off-screen via globalStyles.css */}
      <Box id={CONTAINER_ID} />

      <FormControl size="small" variant="outlined">
        <Select
          value={language}
          onChange={(event) => handleChange(event.target.value)}
          displayEmpty
          startAdornment={
            <TranslateIcon fontSize="small" sx={{ mr: 1, color: "inherit" }} />
          }
          inputProps={{ "aria-label": "Select language" }}
          sx={{
            minWidth: 150,
            backgroundColor: "transparent",
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {TRANSLATE_LANGUAGES.map((item) => (
            <MenuItem key={item.code} value={item.code}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export default GoogleTranslate;
